import type { AdEngagement, AdRecommendation } from '@nono/contract';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { useLocale, useViewMessages } from '@/lib/i18n';
import { evaluateBenchmark, METRIC_INFO } from '@/lib/metric-info';
import { CONFIDENCE_LABEL, renderReason, VERDICT_META } from '@/lib/verdict';
import { type CellFormat, EngagementMetricCell } from './EngagementMetricCell';

/*
 * Per-ad creative-engagement diagnostics, shown inside an expanded Ads row.
 * Three groups — Krok & hold (video funnel), Engasjement & helse, Effektivitet —
 * plus a retention curve. Each cell's value is graded against its benchmark band
 * (metric-info.ts) and its name carries the hover card with formula + advice.
 * Video-only metrics render "kun video" for image ads (engagement.isVideo=false).
 */

const MESSAGES = {
  nb: {
    groupHook: 'Krok & hold',
    groupHealth: 'Engasjement & helse',
    groupCost: 'Effektivitet',
    retention: 'Retensjonskurve (% av visninger)',
    hookRate: 'Krokrate',
    holdRate: 'Hold-rate',
    thruplayRate: 'ThruPlay-rate',
    completionPct: 'Fullføring',
    avgWatchSec: 'Snitt sett-tid',
    engagementRate: 'Engasjement',
    linkCtr: 'Lenke-CTR',
    outboundCtr: 'Utgående CTR',
    frequency: 'Frekvens',
    costPerThruplay: 'Kost / ThruPlay',
    costPer3s: 'Kost / 3s',
  },
  en: {
    groupHook: 'Hook & hold',
    groupHealth: 'Engagement & health',
    groupCost: 'Efficiency',
    retention: 'Retention curve (% of impressions)',
    hookRate: 'Hook rate',
    holdRate: 'Hold rate',
    thruplayRate: 'ThruPlay rate',
    completionPct: 'Completion',
    avgWatchSec: 'Avg watch time',
    engagementRate: 'Engagement',
    linkCtr: 'Link CTR',
    outboundCtr: 'Outbound CTR',
    frequency: 'Frequency',
    costPerThruplay: 'Cost / ThruPlay',
    costPer3s: 'Cost / 3s',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

interface Metric {
  key: keyof Omit<AdEngagement, 'isVideo' | 'retention'>;
  labelKey: keyof Msgs;
  format: CellFormat;
  video: boolean;
}

const HOOK: Metric[] = [
  { key: 'hookRate', labelKey: 'hookRate', format: 'pct', video: true },
  { key: 'holdRate', labelKey: 'holdRate', format: 'pct', video: true },
  { key: 'thruplayRate', labelKey: 'thruplayRate', format: 'pct', video: true },
  { key: 'completionPct', labelKey: 'completionPct', format: 'pct', video: true },
  { key: 'avgWatchSec', labelKey: 'avgWatchSec', format: 'seconds', video: true },
];
const HEALTH: Metric[] = [
  { key: 'engagementRate', labelKey: 'engagementRate', format: 'pct', video: false },
  { key: 'linkCtr', labelKey: 'linkCtr', format: 'pct', video: false },
  { key: 'outboundCtr', labelKey: 'outboundCtr', format: 'pct', video: false },
  { key: 'frequency', labelKey: 'frequency', format: 'ratio', video: false },
];
const COST: Metric[] = [
  { key: 'costPerThruplay', labelKey: 'costPerThruplay', format: 'nok', video: true },
  { key: 'costPer3s', labelKey: 'costPer3s', format: 'nok', video: true },
];

export function AdEngagementPanel({
  engagement,
  recommendation,
}: {
  engagement: AdEngagement;
  recommendation: AdRecommendation;
}) {
  const m = useViewMessages(MESSAGES);
  const isVideo = engagement.isVideo;

  const renderCell = (metric: Metric) => {
    const value = engagement[metric.key];
    const band = METRIC_INFO[metric.key]?.benchmark;
    const unavailable = metric.video && !isVideo;
    return (
      <EngagementMetricCell
        key={metric.key}
        metricKey={metric.key}
        label={m[metric.labelKey]}
        value={value}
        format={metric.format}
        verdict={band ? evaluateBenchmark(value, band) : null}
        unavailable={unavailable}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <RecommendationCard recommendation={recommendation} />
      <Group title={m.groupHook}>{HOOK.map(renderCell)}</Group>
      <Group title={m.groupHealth}>{HEALTH.map(renderCell)}</Group>
      <Group title={m.groupCost}>{COST.map(renderCell)}</Group>
      {isVideo ? <RetentionCurve m={m} retention={engagement.retention} /> : null}
    </div>
  );
}

/** The verdict card: keep/kill/scale + confidence + the engine's reasoning. */
function RecommendationCard({ recommendation }: { recommendation: AdRecommendation }) {
  const { locale } = useLocale();
  const meta = VERDICT_META[recommendation.verdict];
  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3">
      <div className="flex items-center gap-2">
        <Badge tone={meta.tone}>{meta[locale]}</Badge>
        <span className="text-micro text-fg-subtle">
          {CONFIDENCE_LABEL[recommendation.confidence][locale]}
        </span>
      </div>
      <ul className="flex flex-col gap-1">
        {recommendation.reasons.map((r) => (
          <li
            key={r.code}
            className="flex items-start gap-1.5 text-label leading-relaxed text-fg-muted"
          >
            <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-fg-subtle" />
            {renderReason(r, locale)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-micro font-semibold uppercase tracking-wide text-fg-subtle">{title}</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">{children}</div>
    </div>
  );
}

function RetentionCurve({ m, retention }: { m: Msgs; retention: AdEngagement['retention'] }) {
  const steps: { label: string; value: number | null }[] = [
    { label: '25%', value: retention.p25 },
    { label: '50%', value: retention.p50 },
    { label: '75%', value: retention.p75 },
    { label: '100%', value: retention.p100 },
  ];
  // Scale bars to the first quartile (the curve is monotonic decreasing).
  const max = retention.p25 && retention.p25 > 0 ? retention.p25 : 1;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-micro font-semibold uppercase tracking-wide text-fg-subtle">
        {m.retention}
      </div>
      <div className="flex items-end gap-2">
        {steps.map((s) => (
          <div key={s.label} className="flex flex-1 flex-col items-center gap-1">
            <div className="flex h-16 w-full items-end overflow-hidden rounded bg-surface-2">
              <div
                className="w-full rounded-t bg-accent-click/70"
                style={{ height: `${s.value != null ? Math.max(2, (s.value / max) * 100) : 0}%` }}
              />
            </div>
            <span className="text-micro tabular text-fg">
              {s.value != null ? `${s.value}%` : '—'}
            </span>
            <span className="text-micro text-fg-subtle">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
